"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _Dropdown = _interopRequireDefault(require("../Dropdown/Dropdown"));
var _DateOutput = _interopRequireDefault(require("../DateOutput/DateOutput"));
var _RadioGroup = _interopRequireDefault(require("../Radio/RadioGroup"));
var _InputDate = _interopRequireDefault(require("../InputDate/InputDate"));
var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));
var _useLabel = _interopRequireDefault(require("../../hooks/useLabel"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const RANGE_OPTIONS = {
  TODAY: 'TODAY',
  YESTERDAY: 'YESTERDAY',
  LAST_SEVEN_DAYS: 'LAST_SEVEN_DAYS',
  LAST_FOURTEEN_DAYS: 'LAST_FOURTEEN_DAYS',
  LAST_THIRTY_DAYS: 'LAST_THIRTY_DAYS',
  LAST_120_DAYS: 'LAST_120_DAYS',
  CURRENT_WEEK: 'CURRENT_WEEK',
  CURRENT_MONTH: 'CURRENT_MONTH',
  CURRENT_YEAR: 'CURRENT_YEAR',
  LAST_WEEK: 'LAST_WEEK',
  LAST_MONTH: 'LAST_MONTH',
  LAST_YEAR: 'LAST_YEAR',
  NEXT_SEVEN_DAYS: 'NEXT_SEVEN_DAYS',
  NEXT_FOURTEEN_DAYS: 'NEXT_FOURTEEN_DAYS',
  NEXT_THIRTY_DAYS: 'NEXT_THIRTY_DAYS',
  NEXT_MONTH: 'NEXT_MONTH',
  NEXT_SIX_MONTHS: 'NEXT_SIX_MONTHS',
  CUSTOM: 'CUSTOM'
};
const RANGE_OPTIONS_LABEL = {
  TODAY: 'Today',
  YESTERDAY: 'Yesterday',
  LAST_SEVEN_DAYS: 'Last Seven Days',
  LAST_FOURTEEN_DAYS: 'Last Fourteen Days',
  LAST_THIRTY_DAYS: 'Last Thirty Days',
  LAST_120_DAYS: 'Last 120 Days',
  CURRENT_WEEK: 'Current Week',
  CURRENT_MONTH: 'Current Month',
  CURRENT_YEAR: 'Current Year',
  LAST_WEEK: 'Last Week',
  LAST_MONTH: 'Last Month',
  LAST_YEAR: 'Last Year',
  NEXT_SEVEN_DAYS: 'Next Seven Days',
  NEXT_FOURTEEN_DAYS: 'Next Fourteen Days',
  NEXT_THIRTY_DAYS: 'Next Thirty Days',
  NEXT_MONTH: 'Next Month',
  NEXT_SIX_MONTHS: 'Next Six Months',
  CUSTOM: 'Custom'
};

// Format an inputted range to match the required range structure { rangeId, min, max }
const inputRange = (range, options) => {
  let rangeId = null;
  let min = null;
  let max = null;
  const isCustomOption = (range == null ? void 0 : range.rangeId) === RANGE_OPTIONS.CUSTOM;

  // 'rangeId' is always considered first
  if (range != null && range.rangeId && options.includes(range.rangeId) || isCustomOption) {
    rangeId = range.rangeId;
  }
  // 'min' and 'max' are secondly considered if they're both valid (this will set the custom rangeId)
  if ((!(range != null && range.rangeId) || isCustomOption) && range != null && range.min && range != null && range.max) {
    const d1 = new Date(range.min);
    const d2 = new Date(range.max);
    if (!isNaN(d1) && !isNaN(d2)) {
      rangeId = RANGE_OPTIONS.CUSTOM;
      min = d1.getTime();
      max = d2.getTime();
    }
  }
  return {
    rangeId,
    min,
    max
  };
};
const sameDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  if (isNaN(d1) || isNaN(d2)) return false;
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
};
const SelectDateRange = props => {
  const {
    id: controlledId,
    label: initialLabel,
    value: controlledRange,
    name,
    options,
    onChange,
    floatLabel,
    clearable,
    disabled,
    customMessage,
    rangeLabels,
    placeholder,
    locale,
    className,
    ...attributes
  } = props;
  const id = (0, _react.useRef)((0, _useRandomId.default)(controlledId)).current;
  const {
    label
  } = (0, _useLabel.default)(initialLabel, {
    ...props,
    id
  });
  const [range, setRange] = (0, _react.useState)({
    rangeId: null,
    min: null,
    max: null
  });
  const handleChange = function (newRange, withCallback) {
    if (withCallback === void 0) {
      withCallback = true;
    }
    setRange(newRange);
    if (withCallback && typeof onChange === 'function') {
      onChange(newRange);
    }
  };

  // Compute a new date range in (timestamps in milliseconds)
  const handleRangeChange = range => {
    // 'range' is a rangeId (YESTERDAY, etc..)
    if (typeof range === 'string') {
      const rangeId = range.includes('--') ? range.split('--')[1] : range;
      const now = new Date();
      const day = now.getDate();
      const year = now.getFullYear();
      const month = now.getMonth();
      const todayStart = new Date(year, month, day, 0, 0, 0).getTime();
      const todayEnd = new Date(year, month, day, 23, 59, 59, 999).getTime();
      switch (rangeId) {
        case RANGE_OPTIONS.TODAY:
          handleChange({
            min: todayStart,
            max: todayEnd,
            rangeId
          });
          break;
        case RANGE_OPTIONS.YESTERDAY:
          handleChange({
            min: new Date(year, month, day - 1, 0, 0, 0).getTime(),
            max: new Date(year, month, day - 1, 23, 59, 59, 999).getTime(),
            rangeId
          });
          break;
        case RANGE_OPTIONS.LAST_SEVEN_DAYS:
          handleChange({
            min: new Date(year, month, day - 7, 0, 0, 0).getTime(),
            max: todayEnd,
            rangeId
          });
          break;
        case RANGE_OPTIONS.LAST_FOURTEEN_DAYS:
          handleChange({
            min: new Date(year, month, day - 14, 0, 0, 0).getTime(),
            max: todayEnd,
            rangeId
          });
          break;
        case RANGE_OPTIONS.LAST_THIRTY_DAYS:
          handleChange({
            min: new Date(year, month, day - 30, 0, 0, 0).getTime(),
            max: todayEnd,
            rangeId
          });
          break;
        case RANGE_OPTIONS.LAST_120_DAYS:
          handleChange({
            min: new Date(year, month, day - 120, 0, 0, 0).getTime(),
            max: todayEnd,
            rangeId
          });
          break;
        case RANGE_OPTIONS.CURRENT_WEEK:
          const firstDayOfWeek = now.getDate() - now.getDay(); // First day is the day of the month - the day of the week
          const lastDayOfWeek = firstDayOfWeek + 6;
          handleChange({
            min: new Date(year, month, firstDayOfWeek, 0, 0, 0).getTime(),
            max: new Date(year, month, lastDayOfWeek, 23, 59, 59, 999).getTime(),
            rangeId
          });
          break;
        case RANGE_OPTIONS.CURRENT_MONTH:
          handleChange({
            min: new Date(year, month, 1, 0, 0, 0).getTime(),
            max: new Date(year, month + 1, 0, 23, 59, 59, 999).getTime(),
            rangeId
          });
          break;
        case RANGE_OPTIONS.CURRENT_YEAR:
          handleChange({
            min: new Date(year, 0, 1, 0, 0, 0).getTime(),
            max: new Date(year + 1, 0, 0, 23, 59, 59, 999).getTime(),
            rangeId
          });
          break;
        case RANGE_OPTIONS.LAST_WEEK:
          const firstDayOfLastWeek = now.getDate() - now.getDay() - 7; // First day is the day of the month - the day of the week
          const lastDayOfLastWeek = firstDayOfLastWeek + 6;
          handleChange({
            min: new Date(year, month, firstDayOfLastWeek, 0, 0, 0).getTime(),
            max: new Date(year, month, lastDayOfLastWeek, 23, 59, 59, 999).getTime(),
            rangeId
          });
          break;
        case RANGE_OPTIONS.LAST_MONTH:
          handleChange({
            min: new Date(year, month - 1, 1, 0, 0, 0).getTime(),
            max: new Date(year, month, 0, 23, 59, 59, 999).getTime(),
            rangeId
          });
          break;
        case RANGE_OPTIONS.LAST_YEAR:
          handleChange({
            min: new Date(year - 1, 0, 1, 0, 0, 0).getTime(),
            max: new Date(year, 0, 0, 23, 59, 59, 999).getTime(),
            rangeId
          });
          break;
        case RANGE_OPTIONS.NEXT_SEVEN_DAYS:
          handleChange({
            min: todayStart,
            max: new Date(year, month, day + 7, 23, 59, 59, 999).getTime(),
            rangeId
          });
          break;
        case RANGE_OPTIONS.NEXT_FOURTEEN_DAYS:
          handleChange({
            min: todayStart,
            max: new Date(year, month, day + 14, 23, 59, 59, 999).getTime(),
            rangeId
          });
          break;
        case RANGE_OPTIONS.NEXT_THIRTY_DAYS:
          handleChange({
            min: todayStart,
            max: new Date(year, month, day + 30, 23, 59, 59, 999).getTime(),
            rangeId
          });
          break;
        case RANGE_OPTIONS.NEXT_MONTH:
          handleChange({
            min: new Date(year, month + 1, 1, 0, 0, 0).getTime(),
            max: new Date(year, month + 2, 0, 23, 59, 59, 999).getTime(),
            rangeId
          });
          break;
        case RANGE_OPTIONS.NEXT_SIX_MONTHS:
          handleChange({
            min: todayStart,
            max: new Date(year, month + 6, day, 23, 59, 59, 999).getTime(),
            rangeId
          });
          break;
        case 'reset':
          handleChange({
            min: null,
            max: null,
            rangeId: null
          });
          break;
        default:
          handleChange({
            min: null,
            max: null,
            rangeId: RANGE_OPTIONS.CUSTOM
          }, false);
          break;
      }
    }
    // 'range' is a custom date range with min and max values
    else if (Array.isArray(range)) {
      const min = new Date(range[0]).getTime();
      let max = new Date(range[1]);
      max.setHours(23, 59, 59, 999);
      max = max.getTime();
      if (range.length === 2) {
        if (min && max) {
          handleChange({
            min,
            max,
            rangeId: RANGE_OPTIONS.CUSTOM
          });
        } else if (isNaN(min) && isNaN(max)) {
          handleChange({
            min: null,
            max: null,
            rangeId: RANGE_OPTIONS.CUSTOM
          }, false);
        }
      }
    }
  };

  // Only keep options that specified by user else show them all
  const filteredOptions = (0, _react.useMemo)(() => Object.keys(RANGE_OPTIONS).filter(o => Array.isArray(options) ? options.length === 0 ? true : options.includes(o) : true), [options]);
  const rangeOptions = filteredOptions.map(o => {
    if (o === RANGE_OPTIONS.CUSTOM) {
      return {
        label: /*#__PURE__*/_react.default.createElement(_InputDate.default, {
          isRange: true,
          label: (rangeLabels == null ? void 0 : rangeLabels.CUSTOM) || RANGE_OPTIONS_LABEL.CUSTOM,
          onChange: handleRangeChange,
          name: id + "-range-dates",
          locale: locale,
          disabled: range.rangeId !== RANGE_OPTIONS.CUSTOM || disabled,
          value: range.rangeId === RANGE_OPTIONS.CUSTOM ? [range.min, range.max] : [],
          withIcon: false
        }),
        value: RANGE_OPTIONS.CUSTOM,
        id: id + "--" + RANGE_OPTIONS.CUSTOM,
        className: "uk-select--date-range__custom-option"
      };
    } else return {
      label: (rangeLabels == null ? void 0 : rangeLabels[o]) || (RANGE_OPTIONS_LABEL == null ? void 0 : RANGE_OPTIONS_LABEL[o]) || o,
      id: id + "--" + o,
      value: o
    };
  });

  // Update internal 'range' value with 'controlledRange'
  (0, _react.useEffect)(() => {
    const formattedRange = inputRange(controlledRange, filteredOptions);
    const {
      rangeId,
      min,
      max
    } = formattedRange;
    const currentRangeId = range.rangeId;
    if (rangeId !== currentRangeId) {
      if (rangeId === RANGE_OPTIONS.CUSTOM && min && max) {
        handleRangeChange([min, max]);
      } else if (rangeId) {
        handleRangeChange(rangeId);
      } else handleRangeChange('reset');
    }
    // This should only run when the 'controlledRange' prop changes 
    // and not when the internal 'range' changes to prevent useless useEffect executions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledRange, filteredOptions]);
  const containerClasses = (0, _clsx.default)('uk-form-item', 'uk-select', 'uk-select--date-range', disabled && 'disabled is-frozen', clearable && range.min && range.max && 'is-clearable', className);
  return /*#__PURE__*/_react.default.createElement("div", {
    className: containerClasses
  }, label, /*#__PURE__*/_react.default.createElement("div", {
    className: "flex-container"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-form-item__field"
  }, /*#__PURE__*/_react.default.createElement(_Dropdown.default, {
    tabIndex: "-1",
    align: "right",
    disabled: disabled,
    withIcon: !disabled,
    trigger: /*#__PURE__*/_react.default.createElement("div", {
      className: "uk-dropdown__input"
    }, /*#__PURE__*/_react.default.createElement("div", {
      className: "uk-inputtable"
    }, range.min && range.max ? /*#__PURE__*/_react.default.createElement("div", null, /*#__PURE__*/_react.default.createElement(_DateOutput.default, {
      date: range.min,
      locale: locale,
      utc: false
    }), !sameDay(range.min, range.max) && /*#__PURE__*/_react.default.createElement(_react.Fragment, null, ' ', "- ", /*#__PURE__*/_react.default.createElement(_DateOutput.default, {
      date: range.max,
      locale: locale,
      utc: false
    }))) : /*#__PURE__*/_react.default.createElement("div", null, placeholder)), clearable && range.min && range.max && !disabled && /*#__PURE__*/_react.default.createElement("div", {
      "aria-hidden": "true",
      className: "uk-input__clearButton right",
      onClick: () => handleRangeChange('reset')
    }, "\xD7"))
  }, /*#__PURE__*/_react.default.createElement(_RadioGroup.default, _extends({
    outline: true,
    inline: true,
    name: name || id,
    checked: id + "--" + range.rangeId,
    onChange: handleRangeChange,
    className: "uk-form-field",
    disabled: disabled,
    options: rangeOptions
  }, attributes))))));
};
SelectDateRange.propTypes = {
  /**
   * ID of the SelectDateRange (used to bind the label to its input field)
   * NB: the ID will be randomly generated if not set.
   */
  id: _propTypes.default.string,
  /**
   * Name of the SelectDateRange (used instead of the ID when submitted)
   */
  name: _propTypes.default.string,
  /**
   * Array of options used by the SelectDateRange (if left empty will display all available options)
   */
  options: _propTypes.default.arrayOf(_propTypes.default.oneOf(Object.keys(RANGE_OPTIONS))),
  /**
   * Default value of the SelectDateRange
   */
  value: _propTypes.default.oneOfType([_propTypes.default.shape({
    rangeId: _propTypes.default.oneOf(Object.keys(RANGE_OPTIONS)).isRequired
  }), _propTypes.default.shape({
    max: _propTypes.default.number.isRequired,
    // a timestamp in milliseconds
    min: _propTypes.default.number.isRequired // a timestamp in milliseconds
  }), _propTypes.default.shape({})]),
  /**
   * Label of the SelectDateRange
   */
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Range labels for the range options
   */
  rangeLabels: _propTypes.default.shape({
    YESTERDAY: _propTypes.default.string
  }),
  /**
   * SelectDateRange placeholder
   */
  placeholder: _propTypes.default.string,
  /**
   * Set the date formatting depending on the locale
   */
  locale: _propTypes.default.oneOf(['en', 'fr']),
  /**
   * Add a button to clear the SelectDateRange
   */
  clearable: _propTypes.default.bool,
  /**
   * Set the SelectDateRange as disabled
   */
  disabled: _propTypes.default.bool,
  /**
   * Callback function onChange
   */
  onChange: _propTypes.default.func
};
SelectDateRange.defaultProps = {
  id: '',
  name: '',
  options: [],
  value: {},
  label: '',
  rangeLabels: {},
  placeholder: 'Refine',
  locale: 'en',
  clearable: true,
  disabled: false,
  onChange: () => {}
};
var _default = exports.default = SelectDateRange;